import { useState, useRef, useEffect } from 'react';
import { SmilePlus } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';

export const REACTIONS = [
  { type: 'heart', emoji: '❤️', label: "J'adore" },
  { type: 'laugh', emoji: '😂', label: 'Drôle' },
  { type: 'wow', emoji: '😮', label: 'Étonnant' },
  { type: 'sad', emoji: '😢', label: 'Triste' },
  { type: 'clap', emoji: '👏', label: 'Bravo' },
];

const emojiOf = (type) => REACTIONS.find((r) => r.type === type)?.emoji;

const ReactionBar = ({ post, onChange, size = 'sm' }) => {
  const { user, api } = useAuth();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const close = (e) => { if (!wrapRef.current?.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  const reactions = post.reactions || {};
  const total = Object.values(reactions).reduce((a, b) => a + b, 0);
  const top = Object.entries(reactions).filter(([, n]) => n > 0).sort((a, b) => b[1] - a[1]).slice(0, 3);

  const react = async (type) => {
    if (!user) { toast.error('Connectez-vous pour réagir'); return; }
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/posts/${post.post_id}/react`, { type });
      onChange?.({ reactions: data.reactions, user_reaction: data.user_reaction });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur de réaction');
    } finally {
      setBusy(false);
      setOpen(false);
    }
  };

  const iconSize = size === 'lg' ? 'h-5 w-5' : 'h-4 w-4';

  return (
    <div ref={wrapRef} className="relative inline-flex items-center" onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`inline-flex items-center gap-1 rounded-md px-2 py-1.5 text-sm font-medium transition-colors hover:bg-accent/40 ${post.user_reaction ? 'text-primary' : 'text-foreground'}`}
        aria-label="Réagir"
        data-testid={`react-btn-${post.post_id}`}
      >
        {post.user_reaction ? (
          <span className="text-base leading-none" data-testid={`user-reaction-${post.post_id}`}>{emojiOf(post.user_reaction)}</span>
        ) : (
          <SmilePlus className={iconSize} />
        )}
        {total > 0 && (
          <span className="flex items-center gap-0.5" data-testid={`reaction-summary-${post.post_id}`}>
            <span className="text-sm leading-none -space-x-1">{top.map(([t]) => <span key={t}>{emojiOf(t)}</span>)}</span>
            <span className="ml-1 text-xs text-muted-foreground">{total}</span>
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute bottom-full left-0 mb-2 z-50 flex gap-1 rounded-full border border-border bg-card px-2 py-1.5 shadow-xl animate-in fade-in zoom-in-95"
          data-testid={`reaction-picker-${post.post_id}`}
        >
          {REACTIONS.map((r) => (
            <button
              key={r.type}
              type="button"
              title={r.label}
              onClick={() => react(r.type)}
              className={`text-2xl leading-none rounded-full p-1 transition-transform hover:scale-125 ${post.user_reaction === r.type ? 'bg-primary/15 ring-2 ring-primary' : ''}`}
              data-testid={`reaction-${r.type}-${post.post_id}`}
            >
              {r.emoji}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default ReactionBar;
